import * as React from 'react';
import { findDOMNode } from 'react-dom';
import { MdExpandMore, MdExpandLess } from 'react-icons/md';
import { scrollIntoView } from 'app/infrastructure/platform/browser-dom';
import { DialogExpander } from 'ui/common/dialog-expander/dialog-expander';
import { ButtonTertiary } from 'ui';
import styles from './compact-tile.scss';
import { debounceRender } from 'ui/common/debounce-render';
import { ExpanderBackground } from 'ui/common/dialog-expander/expander-background/expander-background';
import { ContextMenu } from 'ui/tile/context-menu';
import { ButtonSecondary, ValidationLevel } from 'ui/common';
import { TileProps, TileState } from 'ui/tile/tile';

class CompactTileComponent extends React.Component<TileProps, TileState> {
  private ref: HTMLElement;
  public static defaultProps: Partial<TileProps> = {
    enabledChildEntityOperations: { remove: false },
    textKeyLabel: ''
  };
  public state: Readonly<TileState> = {
    hideImmediately: false
  };

  private handleSelect = () => {
    this.setState({hideImmediately: false}, () =>
      this.props.onSelectDocumentItem && this.props.onSelectDocumentItem()
    );
    scrollIntoView( findDOMNode(this.ref) as HTMLElement);
  };

  private handleEdit = () => {
    this.props.onEditDocumentItem && this.props.onEditDocumentItem();
  };

  private handleDelete = () => {
    this.props.onDeleteDocumentItem && this.props.onDeleteDocumentItem();
  };

  private handleClose = () => {
    if (this.props.activate) {
       this.setState({hideImmediately: true}, () =>
       this.props.onSelectDocumentItem && this.props.onSelectDocumentItem(null) );
    }
  };

  private renderDetailsButton = (showDetails:boolean) => {
    const {textShowDetails, textHideDetails, validationDescendants} = this.props;

    const childrenHasErrors = validationDescendants &&
      validationDescendants.length &&
      validationDescendants.some(v => v.level === ValidationLevel.error);

    const childrenHasWarnings = validationDescendants &&
      validationDescendants.length &&
      validationDescendants.some(v => v.level === ValidationLevel.warning);

    const validationClass = childrenHasErrors ?
      styles.hasChildErrors :
      (childrenHasWarnings ? styles.hasChildWarnings : '');

    return <div className={styles.buttonContainer}>
              <ButtonTertiary
                classNames={`${styles.detailsButton} ${validationClass}`}
                onClick={showDetails ? this.handleSelect : this.handleClose}>
                  {showDetails ?
                    <>{textShowDetails}<MdExpandMore/></> :
                    <>{textHideDetails}<MdExpandLess/></> }
              </ButtonTertiary>
            </div>;
  };

  private addRef = (el: HTMLElement) => this.ref = el;

  public render() {
    const {
      componentId,
      details,
      children,
      activate,
      primaryKey,
      showPrimaryKey = true,
      showDetailsButton = true,
      showDetailsEditButton = false,
      textKeyLabel,
      textDone,
      textDetailsEditButton,
      print,
      expandAll
    } = this.props;
    // Detail dialog styles and title when primaryKey prop is provided
    const primaryKeyValue = (primaryKey && primaryKey.value) || '';
    const dialogTitle = `${textKeyLabel} #${primaryKeyValue}`;

    const canActivate = showDetailsButton && !this.state.hideImmediately;
    const isActivated = canActivate && activate;
    const shouldRenderOpenDetailView = details && (print || expandAll || isActivated);

    const shouldRenderDetailsEditButton = shouldRenderOpenDetailView && showDetailsEditButton;
    const detailsEditButton = shouldRenderDetailsEditButton ?
      <ButtonSecondary classNames={styles.editButton} onClick={this.handleEdit}>{textDetailsEditButton}</ButtonSecondary> : null;

    // From above logical evaluations; construct a expander to display details
    const detailView = shouldRenderOpenDetailView ?
                                <DialogExpander
                                  expandAll={expandAll}
                                  onClose={this.handleClose}
                                  title={dialogTitle}
                                  hideTitle={expandAll}
                                  textDone={textDone}
                                  extraFooterActions={[detailsEditButton]}
                                  className="tile-details-view">
                                  {expandAll && <ContextMenu {...this.props} onDelete={this.handleDelete}/>}
                                  {details}
                                  {expandAll && <div className={styles.editButtonExpandAll}>{detailsEditButton}</div>}
                                </DialogExpander>
                              : null;

    const expandDetailsButton = details && this.renderDetailsButton(!shouldRenderOpenDetailView);

    const wrapperStyle = `${styles.compactTileWrapper} ${shouldRenderOpenDetailView && !expandAll ? styles.activeTile : ''}`;

    if (expandAll && details) {
      // Only show details - no header, no context menu - no modal backdrop
      return <div id={componentId} className={`${wrapperStyle} compact compact-tile`} ref={this.addRef}>
        {detailView}
      </div>;
    }

    return  <>
              {/* When expanded/open - render the close handle before the tile */}
              {shouldRenderOpenDetailView ? <div tabIndex={0} onFocus={this.handleClose}/> : ''}

              <div id={componentId} className={`${wrapperStyle} compact compact-tile`} ref={this.addRef}>
                  <div className={`${styles.compactTile}`}>
                      {/* Tile content - header, context menu and children*/}
                      <ContextMenu {...this.props} className={styles.contextMenu} onDelete={this.handleDelete}/>
                      <div className={`${styles.contentWrapper} ${showPrimaryKey ? styles.showKey : ''}`}>
                        {children}
                      </div>
                      {/* Button to expand is not part of the content - but rendered below */}
                      {showDetailsButton && expandDetailsButton}
                  </div>
                  {/* If expanded (active, expand-all, print) - renders the details */}
                  {detailView}
                  {/* Modal backdrop */}
                  {(shouldRenderOpenDetailView && !(print || expandAll)) && <ExpanderBackground onClick={this.handleClose}/>}
                </div>
            </>;
  }
}

const t = debounceRender(CompactTileComponent);
export {t as CompactTile};
